import { LandingHeader } from "@/components/lp/LandingHeader";
import { LandingFooter } from "@/components/lp/LandingFooter";
import { LPHero } from "@/components/lp/LPHero";
import { WhyInvest, MarketProof, NewToFitness } from "@/components/lp/Invest";
import { FactoryDirect } from "@/components/lp/FactoryDirect";
import { PurchaseProcess } from "@/components/lp/PurchaseProcess";
import { FeaturedEquipment, BusinessTypes } from "@/components/lp/Equipment";
import { DetailsDifference } from "@/components/lp/DetailsDifference";
import { WhyYanre } from "@/components/lp/WhyYanre";
import { Founder } from "@/components/yanre/Founder";
import { CustomerCases } from "@/components/lp/CustomerCases";
import { Logistics, AfterSales } from "@/components/lp/Logistics";
import { ProjectBuilderForm } from "@/components/lp/ProjectBuilderForm";
import { CTAStrip } from "@/components/lp/CTAStrip";
import { WhatsAppFloat } from "@/components/lp/WhatsAppFloat";
import { Marquee } from "@/components/yanre/Marquee";
import { track } from "@/lib/track";
import { useLocale } from "@/i18n/LocaleContext";
import { useSeo } from "@/hooks/useSeo";

// APPROVED official hero asset v2 (2026-07): Dubai premium gym, equipment
// concentrated right — desktop shows the full cinematic composition, mobile
// shifts focal position to keep equipment + skyline. Masters untouched in /media.
const HERO_MEDIA = {
  webp768: "/media/hero-dubai-768.webp",
  webp1280: "/media/hero-dubai-1280.webp",
  webp1920: "/media/hero-dubai-1920.webp",
  fallback: "/media/hero-dubai-1280.jpg",
  alt: "YANRE FITNESS premium gym with commercial strength equipment overlooking the Dubai skyline",
  desktopPosition: "center center",
  mobilePosition: "68% 50%",
};

// SECTION 06 — final official YANRE assets (2026-07), masters untouched in /media.
const EQUIPMENT = [
  { category: "Selectorized Strength", series: "73 Tough Series", statement: "Selectorized strength engineered for professional daily use.", image: "/media/equip-selectorized-900.webp", srcSet: "/media/equip-selectorized-900.webp 900w, /media/equip-selectorized-1600.webp 1600w", alt: "YANRE 73 Series selectorized strength machine in a premium gym" },
  { category: "Plate Loaded", series: "Plate-Loaded Power", statement: "Plate-loaded machines built around natural movement.", image: "/media/equip-plate-loaded-900.webp", srcSet: "/media/equip-plate-loaded-900.webp 900w, /media/equip-plate-loaded-1600.webp 1600w", alt: "YANRE plate-loaded machine under heavy training load" },
  { category: "Dual Function", series: "C4D Dual Function", statement: "Two stations in one footprint — maximum training density.", image: "/media/equip-dual-function-900.webp", srcSet: "/media/equip-dual-function-900.webp 900w, /media/equip-dual-function-1600.webp 1600w", alt: "YANRE C4D dual function strength line in a commercial gym" },
  { category: "Commercial Cardio", series: "Commercial Cardio", statement: "Cardio built for nonstop commercial environments.", image: "/media/equip-cardio-900.webp", srcSet: "/media/equip-cardio-900.webp 900w, /media/equip-cardio-1600.webp 1600w", alt: "YANRE commercial cardio range — treadmills, bikes and ellipticals" },
];

// Details — all 8 official YANRE macro assets installed (Pass 1+2, 2026-07);
// untouched masters archived in /media.
const DETAILS = [
  { title: "Original Engineering", statement: "Design developed by YANRE.", image: "/media/detail-original-engineering-360-grip-v2-900.webp", srcSet: "/media/detail-original-engineering-360-grip-v2-900.webp 900w, /media/detail-original-engineering-360-grip-v2-1400.webp 1400w" },
  { title: "Patented Design", statement: "Original, patented equipment design.", metric: "200+ Patents", image: "/media/detail-patented-design-900.webp", srcSet: "/media/detail-patented-design-900.webp 900w, /media/detail-patented-design-1400.webp 1400w" },
  { title: "Frame Construction", statement: "Close-up frame and tube engineering.", image: "/media/detail-frame-construction-900.webp", srcSet: "/media/detail-frame-construction-900.webp 900w, /media/detail-frame-construction-1400.webp 1400w" },
  { title: "Curved Frame Design", statement: "Frame geometry shaped around movement.", image: "/media/detail-curved-frame-design-900.webp", srcSet: "/media/detail-curved-frame-design-900.webp 900w, /media/detail-curved-frame-design-1400.webp 1400w" },
  { title: "Upholstery & Cushion", statement: "Professional upholstery, stitching and finish.", image: "/media/detail-injected-foam-upholstery-900.webp", srcSet: "/media/detail-injected-foam-upholstery-900.webp 900w, /media/detail-injected-foam-upholstery-1400.webp 1400w" },
  { title: "Cable / Pulley / Guide System", statement: "Precision movement components.", image: "/media/detail-cable-pulley-system-900.webp", srcSet: "/media/detail-cable-pulley-system-900.webp 900w, /media/detail-cable-pulley-system-1400.webp 1400w" },
  { title: "Biomechanics", statement: "Pivots and motion paths designed around the body.", image: "/media/detail-biomechanics-900.webp", srcSet: "/media/detail-biomechanics-900.webp 900w, /media/detail-biomechanics-1400.webp 1400w" },
  { title: "Commercial-Grade Construction", statement: "Built for durability and daily professional use.", image: "/media/detail-commercial-grade-construction-900.webp", srcSet: "/media/detail-commercial-grade-construction-900.webp 900w, /media/detail-commercial-grade-construction-1400.webp 1400w" },
];

// Case media (2026-07): all three cases complete — Morocco, Honduras and USA
// each use their official covers and uploaded MP4s, lazy-loaded in the modal.
const CASES = [
  { country: "Morocco", title: "Premium Look. Professional Setup.", description: "", videoUrl: "/media/case-morocco-video.mp4", poster: "/media/case-morocco-cover-900.webp", region: "Africa" },
  { country: "Honduras", title: "Simple Layout. Solid Setup.", description: "", videoUrl: "/media/case-honduras-video.mp4", poster: "/media/case-honduras-cover-900.webp", region: "Central America" },
  { country: "USA", title: "Functional Style. Professional Equipment.", description: "", videoUrl: "/media/case-usa-video.mp4", poster: "/media/case-usa-cover-900.webp", region: "North America" },
];

const scrollToBuilder = () =>
  document.getElementById("project-builder")?.scrollIntoView({ behavior: "smooth" });

export default function OpenYourGym() {
  const { locale, t } = useLocale();
  useSeo({
    locale,
    titleKey: "seo.title",
    descKey: "seo.description",
    titleFallback: "Open Your Gym — Factory-Direct Equipment | YANRE FITNESS EUROPE",
    descFallback: "From private studios to large commercial fitness facilities — professional YANRE equipment, project consulting, international logistics and factory-backed after-sales.",
  });

  // Dynamic sequential section numbering — removing/reordering a section
  // renumbers the page automatically. No hard-coded numbers.
  let n = 0;
  const num = () => String(++n).padStart(2, "0");

  const goBuilder = (placement) => {
    track("cta_start_project", { placement });
    scrollToBuilder();
  };

  return (
    <div className="bg-carbon-950 font-sans text-titanium" data-testid="open-your-gym-page">
      <LandingHeader />
      <LPHero
        label={t("hero.label", "Open Your Gym")}
        lines={[
          t("hero.line1", "Build your gym."),
          <span key="m">
            {t("hero.line2a", "Buy direct from the")} <span className="text-signal">{t("hero.line2b", "manufacturer.")}</span>
          </span>,
        ]}
        copy={t("hero.copy", "From private studios to large commercial fitness facilities — professional YANRE equipment, personalized project consulting, international logistics support and factory-backed after-sales.")}
        primaryCta={t("hero.ctaPrimary", "Start Your Gym Project")}
        secondaryCta={t("hero.ctaSecondary", "Talk to a Specialist")}
        onPrimary={() => goBuilder("hero_primary")}
        onSecondary={() => goBuilder("hero_secondary")}
        trustItems={[0, 1, 2, 3].map((i) =>
          t(`hero.trust.${i}`, ["Factory Direct", "Project Consulting", "International Logistics", "Factory-Backed Warranty"][i])
        )}
        mediaType="image"
        media={HERO_MEDIA}
      />
      <Marquee
        items={["Precision", "Performance", "Confidence", "Engineering", "Since 1997"]}
        className="border-y border-carbon-700 bg-carbon-950 py-6"
      />
      <WhyInvest index={num()} />
      <MarketProof index={num()} />
      <CTAStrip label={t("strip.1", "Ready to talk about your project?")} cta={t("strip.cta", "Start Your Project")} onCta={scrollToBuilder} />
      <NewToFitness index={num()} />
      <FactoryDirect index={num()} />
      <PurchaseProcess index={num()} />
      <CTAStrip label={t("strip.2", "Know what you need? Let's talk.")} cta={t("strip.cta", "Start Your Project")} onCta={scrollToBuilder} />
      <FeaturedEquipment items={EQUIPMENT} index={num()} />
      <DetailsDifference details={DETAILS} index={num()} />
      <CTAStrip label={t("strip.3", "Ready to talk about your project?")} cta={t("strip.cta", "Start Your Project")} onCta={scrollToBuilder} />
      <BusinessTypes index={num()} />
      <WhyYanre index={num()} />
      <Founder index={num()} onCtaClick={() => document.getElementById("equipment")?.scrollIntoView({ behavior: "smooth" })} />
      <CustomerCases cases={CASES} index={num()} />
      <CTAStrip label={t("strip.1", "Ready to talk about your project?")} cta={t("strip.cta", "Start Your Project")} onCta={scrollToBuilder} />
      <Logistics index={num()} onCta={scrollToBuilder} />
      <AfterSales index={num()} />
      <ProjectBuilderForm index={num()} />
      <LandingFooter />
      <WhatsAppFloat page="open-your-gym" />
    </div>
  );
}
